import React, { useContext, useEffect, useMemo, useState } from "react";
import Modal from "@mui/material/Modal";
import AuthContext from "../context/userAuth.context";
import { aiApi } from "../Utils/aiTemplate/aiApi";
import { loadAIAccess } from "../AIStudio/AIAccess";

// Admin Panel > "AI Studio access": who has AI Studio (everyone / customers who bought it / nobody),
// what customers without it see (a locked menu with an upgrade message, or nothing), and where
// AI Studio opens (the page inside Toons, or later the new app's address). Customers can also be
// given AI Studio or have it taken off by hand. Nothing is deleted: "Remove" only takes them off the list.
// API: api/AI/Access/* (AnimationStudio2_API, Services/AI/AIAccess.cs, Database/007_ai_access.sql).

const BLUE = "#296AFF";
const label = { display: "block", fontSize: 12, fontWeight: 600, color: "#5b6476", margin: "14px 0 4px" };
const input = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #dfe5f2",
  borderRadius: 6,
  padding: "7px 9px",
  fontSize: 14,
  fontFamily: "inherit",
  color: "#1d2433",
};
const button = (primary, disabled) => ({
  height: 34,
  padding: "0 14px",
  borderRadius: 8,
  border: primary ? "none" : "1px solid #dfe5f2",
  background: disabled ? "#c9d3e6" : primary ? BLUE : "#fff",
  color: primary ? "#fff" : "#1d2433",
  fontSize: 13,
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: disabled ? "default" : "pointer",
  whiteSpace: "nowrap",
});
const card = { border: "1px solid #e6ebf5", borderRadius: 10, padding: "4px 16px 16px", marginBottom: 16, background: "#fff" };
const h3 = { margin: "14px 0 2px", fontSize: 15, color: "#1d2433" };
const hint = { fontSize: 12, color: "#7a8399", marginTop: 4 };
const choice = (on) => ({
  display: "flex",
  gap: 10,
  alignItems: "flex-start",
  padding: "9px 11px",
  border: "1px solid " + (on ? BLUE : "#dfe5f2"),
  background: on ? "#f2f6ff" : "#fff",
  borderRadius: 8,
  cursor: "pointer",
  marginTop: 6,
  fontSize: 14,
});
const chip = (on) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "5px 10px",
  margin: "4px 6px 0 0",
  borderRadius: 999,
  border: "1px solid " + (on ? BLUE : "#dfe5f2"),
  background: on ? "#f2f6ff" : "#fff",
  fontSize: 13,
  cursor: "pointer",
});

const MODES = [
  { key: "all", title: "Everyone", text: "Every customer has AI Studio (as Toons works today)." },
  { key: "purchased", title: "Only customers who bought it", text: "Customers with one of the products or plans below, or added by hand." },
  { key: "off", title: "Nobody", text: "AI Studio is hidden for all customers, except those given it by hand below. Admins still see it." },
];
const LOCKED = [
  { key: "upgrade", title: "Show it with a lock", text: "The menu shows AI Studio with the premium icon; a click shows the upgrade message." },
  { key: "hide", title: "Hide it", text: "No AI Studio menu, dashboard banner or Create card for them." },
];
const REASONS = {
  admin: "is an admin",
  everyone: "everyone has AI Studio",
  given: "was given AI Studio by hand",
  plan: "is on a plan that includes AI Studio",
  purchase: "bought one of the AI Studio products",
  removed: "had AI Studio taken off by hand",
  off: "AI Studio is off for everybody",
  not_purchased: "has not bought AI Studio",
};

const dateText = (iso) => {
  if (!iso) return "";
  const d = new Date(/Z|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + "Z");
  // End dates are stored as the end of that day in UTC: shown as that day.
  return isNaN(d) ? "" : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
};
const listText = (arr) => (arr || []).join(", ");
const toList = (text) =>
  String(text || "")
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
const toggle = (text, value) => {
  const list = toList(text);
  const has = list.some((v) => v.toLowerCase() === value.toLowerCase());
  return (has ? list.filter((v) => v.toLowerCase() !== value.toLowerCase()) : list.concat(value)).join(", ");
};
const inList = (text, value) => toList(text).some((v) => v.toLowerCase() === String(value).toLowerCase());

export default function AIAccessAdmin({ open, onClose }) {
  const { authenticationData } = useContext(AuthContext);
  const api = authenticationData && authenticationData.ApiBaseURL;

  const [form, setForm] = useState(null); // settings as edited
  const [saved, setSaved] = useState(null); // settings as stored
  const [installed, setInstalled] = useState(true);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [person, setPerson] = useState({ email: "", allow: true, expiresAt: "", note: "" });
  const [checkEmail, setCheckEmail] = useState("");
  const [checked, setChecked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null); // { kind, text }

  const fromServer = (s) => ({
    mode: s.mode || "all",
    productIds: listText(s.productIds),
    accessLevels: listText(s.accessLevels),
    whenLocked: s.whenLocked || "upgrade",
    upgradeUrl: s.upgradeUrl || "",
    studioUrl: s.studioUrl || "",
  });
  const dirty = form && saved && JSON.stringify(form) !== JSON.stringify(saved);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadUsers = (q = search) =>
    aiApi.accessUsers(api, q.trim()).then((res) => res.status && setUsers(res.users || []));

  useEffect(() => {
    if (!open || !api) return;
    setBusy(true);
    aiApi.accessSettings(api).then((res) => {
      setBusy(false);
      if (!res.status) {
        setMessage({
          kind: "error",
          text: res.code === "not_deployed" ? "The API on the server does not have AI Studio access yet." : res.message,
        });
        return;
      }
      setInstalled(res.settings.installed !== false);
      const f = fromServer(res.settings);
      setForm(f);
      setSaved(f);
      setProducts(res.products || []);
      if (res.settings.installed !== false) loadUsers("");
    });
  }, [open, api]);

  const plans = useMemo(() => {
    const seen = [];
    products.forEach((p) => p.type && !seen.includes(p.type) && seen.push(p.type));
    return seen;
  }, [products]);

  const close = () => {
    if (dirty && !window.confirm("Leave without saving the AI Studio access settings?")) return;
    onClose && onClose();
  };

  const save = async () => {
    setBusy(true);
    setMessage(null);
    const res = await aiApi.saveAccessSettings(api, form);
    setBusy(false);
    if (!res.status) return setMessage({ kind: "error", text: res.message || "Could not save." });
    const f = fromServer(res.settings);
    setForm(f);
    setSaved(f);
    loadAIAccess(api, true); // this admin's menu follows right away
    setMessage({ kind: "ok", text: "Saved. Customers see the change within 5 minutes (or when they reload Toons)." });
  };

  const addPerson = async () => {
    setBusy(true);
    setMessage(null);
    const res = await aiApi.saveAccessUser(api, person);
    setBusy(false);
    if (!res.status) return setMessage({ kind: "error", text: res.message || "Could not save." });
    setMessage({ kind: "ok", text: `${person.email.trim()} ${person.allow ? "now has" : "no longer has"} AI Studio.` });
    setPerson({ email: "", allow: true, expiresAt: "", note: "" });
    loadUsers();
  };

  const removePerson = async (email) => {
    if (!window.confirm(`Take ${email} off the list? Their purchase or plan decides again.`)) return;
    setBusy(true);
    const res = await aiApi.removeAccessUser(api, email);
    setBusy(false);
    if (!res.status) return setMessage({ kind: "error", text: res.message || "Could not update." });
    loadUsers();
  };

  const check = async () => {
    setChecked(null);
    const res = await aiApi.checkAccess(api, checkEmail.trim());
    setChecked(res);
  };

  const upgradeChoices = products.filter((p) => p.salesPage && inList(form && form.productIds, p.pfid));

  return (
    <Modal open={!!open} onClose={close}>
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(860px, 96vw)",
          height: "min(860px, 92vh)",
          background: "#f6f8fc",
          borderRadius: 12,
          boxShadow: "0 20px 60px rgba(0,0,0,.25)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          fontFamily: "inherit",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", background: "#fff", borderBottom: "1px solid #e6ebf5" }}>
          <span className="material-icons" style={{ color: "#7c3aed" }}>auto_awesome</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#1d2433" }}>AI Studio access</div>
            <div style={{ fontSize: 12, color: "#7a8399" }}>Show or hide AI Studio for customers, and sell it as an upgrade.</div>
          </div>
          <button style={button(true, busy || !dirty || !installed)} disabled={busy || !dirty || !installed} onClick={save}>
            Save
          </button>
          <button style={button(false)} onClick={close} aria-label="Close">
            Close
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 18 }}>
          {message && (
            <div
              role="status"
              style={{
                marginBottom: 14,
                padding: "9px 12px",
                borderRadius: 8,
                fontSize: 13,
                background: message.kind === "ok" ? "#e8f7ee" : "#fdecec",
                color: message.kind === "ok" ? "#13703a" : "#a1261d",
              }}
            >
              {message.text}
            </div>
          )}
          {!installed && (
            <div style={{ marginBottom: 14, padding: "9px 12px", borderRadius: 8, fontSize: 13, background: "#fff6e0", color: "#7a5300" }}>
              Run <b>Database/007_ai_access.sql</b> on the server first. Until then everyone has AI Studio, as before.
            </div>
          )}
          {!form ? (
            <div style={{ padding: 40, textAlign: "center", color: "#7a8399" }}>{busy ? "Loading…" : ""}</div>
          ) : (
            <>
              <div style={card}>
                <h3 style={h3}>Who has AI Studio</h3>
                {MODES.map((m) => (
                  <label key={m.key} style={choice(form.mode === m.key)}>
                    <input type="radio" name="ais-mode" checked={form.mode === m.key} onChange={() => set("mode", m.key)} style={{ marginTop: 3 }} />
                    <span>
                      <b>{m.title}</b>
                      <div style={hint}>{m.text}</div>
                    </span>
                  </label>
                ))}

                {form.mode === "purchased" && (
                  <>
                    <span style={label}>Products that unlock it (JVZoo)</span>
                    {products.length ? (
                      <div>
                        {products.map((p) => (
                          <span key={p.pfid} style={chip(inList(form.productIds, p.pfid))} onClick={() => set("productIds", toggle(form.productIds, p.pfid))}>
                            <input type="checkbox" readOnly checked={inList(form.productIds, p.pfid)} />
                            {p.name || "Product"} <span style={{ color: "#7a8399" }}>· {p.type} · {p.pfid}</span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div style={hint}>The product list could not be read; type the product ids below.</div>
                    )}
                    <input
                      style={{ ...input, marginTop: 8 }}
                      value={form.productIds}
                      onChange={(e) => set("productIds", e.target.value)}
                      placeholder="Product ids, e.g. 412233, 412240"
                    />
                    <div style={hint}>
                      A new AI Studio product in ProductFunnel should have the lowest Mapping, so it does not change the
                      customer's plan (their Access level) for the other features.
                    </div>

                    <span style={label}>Plans that include it</span>
                    <div>
                      {plans.map((t) => (
                        <span key={t} style={chip(inList(form.accessLevels, t))} onClick={() => set("accessLevels", toggle(form.accessLevels, t))}>
                          <input type="checkbox" readOnly checked={inList(form.accessLevels, t)} /> {t}
                        </span>
                      ))}
                    </div>
                    <input
                      style={{ ...input, marginTop: 8 }}
                      value={form.accessLevels}
                      onChange={(e) => set("accessLevels", e.target.value)}
                      placeholder="Plans, e.g. US3, US5"
                    />
                    <div style={hint}>Agency sub-accounts have the plan US1.</div>
                  </>
                )}
              </div>

              {form.mode !== "all" && (
                <div style={card}>
                  <h3 style={h3}>Customers without it</h3>
                  {LOCKED.map((m) => (
                    <label key={m.key} style={choice(form.whenLocked === m.key)}>
                      <input type="radio" name="ais-locked" checked={form.whenLocked === m.key} onChange={() => set("whenLocked", m.key)} style={{ marginTop: 3 }} />
                      <span>
                        <b>{m.title}</b>
                        <div style={hint}>{m.text}</div>
                      </span>
                    </label>
                  ))}
                  <span style={label}>Upgrade page</span>
                  <input style={input} value={form.upgradeUrl} onChange={(e) => set("upgradeUrl", e.target.value)} placeholder="https://toons.ai/l/..." />
                  {upgradeChoices.length > 0 && (
                    <div style={hint}>
                      Use the sales page of:{" "}
                      {upgradeChoices.map((p) => (
                        <button
                          key={p.pfid}
                          type="button"
                          onClick={() => set("upgradeUrl", p.salesPage)}
                          style={{ border: 0, background: "none", padding: 0, marginRight: 10, color: BLUE, cursor: "pointer", font: "inherit" }}
                        >
                          {p.name || p.pfid}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div style={card}>
                <h3 style={h3}>Where AI Studio opens</h3>
                <span style={label}>New AI Studio app address</span>
                <input style={input} value={form.studioUrl} onChange={(e) => set("studioUrl", e.target.value)} placeholder="Empty = the AI Studio page inside Toons" />
                <div style={hint}>Leave empty for now. When the new app is live, put its address here and the AI Studio menu opens it.</div>
              </div>

              {installed && (
                <div style={card}>
                  <h3 style={h3}>Customers added by hand</h3>
                  <div style={hint}>Give AI Studio to a customer (e.g. a reviewer, a bonus or a trial), or take it off. This wins over their purchase and over "Nobody".</div>
                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0,2fr) minmax(0,1.2fr) minmax(0,1.2fr)", gap: 8, marginTop: 10 }}>
                    <input style={input} value={person.email} onChange={(e) => setPerson({ ...person, email: e.target.value })} placeholder="Login e-mail" />
                    <select style={input} value={person.allow ? "1" : "0"} onChange={(e) => setPerson({ ...person, allow: e.target.value === "1" })}>
                      <option value="1">Give AI Studio</option>
                      <option value="0">Take AI Studio off</option>
                    </select>
                    <input style={input} type="date" value={person.expiresAt} onChange={(e) => setPerson({ ...person, expiresAt: e.target.value })} title="Until (optional)" />
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <input style={input} value={person.note} maxLength={200} onChange={(e) => setPerson({ ...person, note: e.target.value })} placeholder="Note (optional), e.g. 7-day trial" />
                    <button style={button(true, busy || !person.email.trim())} disabled={busy || !person.email.trim()} onClick={addPerson}>
                      Save customer
                    </button>
                  </div>

                  <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                    <input
                      style={input}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && loadUsers()}
                      placeholder="Search e-mail or note"
                    />
                    <button style={button(false)} onClick={() => loadUsers()}>
                      Search
                    </button>
                  </div>
                  {users.length === 0 ? (
                    <div style={{ ...hint, marginTop: 10 }}>No customers added by hand.</div>
                  ) : (
                    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 10, fontSize: 13 }}>
                      <thead>
                        <tr style={{ textAlign: "left", color: "#5b6476" }}>
                          <th style={{ padding: "6px 4px" }}>E-mail</th>
                          <th style={{ padding: "6px 4px" }}>AI Studio</th>
                          <th style={{ padding: "6px 4px" }}>Until</th>
                          <th style={{ padding: "6px 4px" }}>Note</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => {
                          const ended = u.expiresAt && new Date(u.expiresAt) < new Date();
                          return (
                            <tr key={u.id} style={{ borderTop: "1px solid #eef1f7" }}>
                              <td style={{ padding: "6px 4px", wordBreak: "break-all" }}>{u.email}</td>
                              <td style={{ padding: "6px 4px", color: ended ? "#7a8399" : u.allow ? "#13703a" : "#a1261d", fontWeight: 600 }}>
                                {ended ? "Ended" : u.allow ? "Given" : "Taken off"}
                              </td>
                              <td style={{ padding: "6px 4px" }}>{dateText(u.expiresAt) || "—"}</td>
                              <td style={{ padding: "6px 4px", color: "#5b6476" }}>{u.note}</td>
                              <td style={{ padding: "6px 4px", textAlign: "right" }}>
                                <button style={button(false, busy)} disabled={busy} onClick={() => removePerson(u.email)}>
                                  Remove
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              <div style={card}>
                <h3 style={h3}>Check a customer</h3>
                <div style={hint}>Uses the saved settings (save first after a change).</div>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <input
                    style={input}
                    value={checkEmail}
                    onChange={(e) => setCheckEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && checkEmail.trim() && check()}
                    placeholder="Login e-mail"
                  />
                  <button style={button(false, !checkEmail.trim())} disabled={!checkEmail.trim()} onClick={check}>
                    Check
                  </button>
                </div>
                {checked && (
                  <div style={{ marginTop: 10, fontSize: 14 }}>
                    {checked.status ? (
                      <>
                        <b style={{ color: checked.allowed ? "#13703a" : "#a1261d" }}>{checked.allowed ? "Has AI Studio" : "No AI Studio"}</b>
                        {" — "}
                        {checked.email} {REASONS[checked.reason] || checked.reason}
                        {checked.plan ? ` (plan ${checked.plan})` : ""}.
                      </>
                    ) : (
                      <span style={{ color: "#a1261d" }}>{checked.message}</span>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
